/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * Main App component that serves as the entry point for the application
 * Handles rendering of different application variants based on device type and configuration
 * @module App
 */

import PropTypes from "prop-types";
import React, { Suspense, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import LoadingLogo from "./components/LoadingLogo";
import { useGetBetTypes } from "./hooks/bettype-hooks";
import { getAuthDesktopView, getAuthMobileView } from "./redux/reselect/auth-selector";
import { useThirdPartyBalance } from "./utils/AppUtils";

import withCms from "hocs/withCms";
import { useAssets } from "hooks/assets-hooks";
import { useBalance } from "hooks/balance-hooks";
import { useSports } from "hooks/sports-hooks";
import { useWindowSize } from "hooks/window-hooks";
import { getAppDesktop, getAppMobile } from "utils/app";

/**
 * Lazily loaded application components
 * These are loaded only when needed based on application configuration
 */
const SlipstreamApp = React.lazy(() => import("applications/slipstream/SlipstreamApp"));
const BetPointApp = React.lazy(() => import("applications/betpoint/BetPointApp"));

/**
 * PropTypes for the App component
 * @type {Object}
 */
const propTypes = {
  /** Flag indicating if BetPoint app variant should be loaded */
  isBetPoint: PropTypes.bool.isRequired,
  /** Flag indicating if the app is running on a mobile device */
  isMobileDevice: PropTypes.bool.isRequired,
  /** Flag indicating if Slipstream app variant should be loaded */
  isSlipstream: PropTypes.bool.isRequired,
};

/**
 * Breakpoint configuration for responsive design
 * @see https://github.com/flexdinesh/react-socks
 * { xsmall: 0 }, // all mobile devices
 * { small: 576 }, // mobile devices (not sure which one's this big)
 * { medium: 768 }, // ipad, ipad pro, ipad mini, etc
 * { large: 992 }, // smaller laptops
 * { xlarge: 1200 } // laptops and desktops
 */

/**
 * Main App component that handles device-specific rendering and data loading
 * @param {Object} props - Component props
 * @param {boolean} props.isBetPoint - Whether to render BetPoint variant
 * @param {boolean} props.isMobileDevice - Whether app is running on mobile device
 * @param {boolean} props.isSlipstream - Whether to render Slipstream variant
 * @returns {React.ReactElement} Rendered App component
 */
const App = ({ isBetPoint, isMobileDevice, isSlipstream }) => {
  // Obtain the CMS configuration
  const dispatch = useDispatch();

  // Used for iframe mode, where we will notify the parent on our size
  useWindowSize();

  const authMobileView = useSelector(getAuthMobileView);
  const authDesktopViewType = useSelector(getAuthDesktopView);

  // Switch case based on CMS data...  (but we can override via env variables for dev purposes).
  const [mobileViewType, setMobileViewType] = useState(authMobileView);

  // If the user is logged in, load the balance upon page refresh or initial login...
  useBalance(dispatch);

  // Third party balance
  useThirdPartyBalance(dispatch);

  // If we don't know about the sports, ask for them now...
  useSports(isBetPoint, isMobileDevice, isSlipstream, dispatch);

  // If we don't know about the bet types, ask for them now...
  useGetBetTypes(dispatch);

  useAssets(dispatch);

  /**
   * Effect to sync mobile view type with auth state when available
   */
  useLayoutEffect(() => {
    if (isMobileDevice && authMobileView) {
      setMobileViewType(authMobileView);
    }
  }, [isMobileDevice, authMobileView]);

  if (isMobileDevice) {
    // Mobile - Switch case based on CMS data...
    // if `cms` still loading, display loading screen.
    if (!mobileViewType) {
      return <LoadingLogo />;
    }

    // Get the appropriate mobile app variant based on configuration
    const AppMobile = getAppMobile(mobileViewType);

    return (
      <Suspense fallback={<LoadingLogo />}>
        <AppMobile />
      </Suspense>
    );
  }

  if (isSlipstream) {
    return (
      <Suspense fallback={<LoadingLogo />}>
        <SlipstreamApp />
      </Suspense>
    );
  }

  if (isBetPoint) {
    return (
      <Suspense fallback={<LoadingLogo />}>
        <BetPointApp />
      </Suspense>
    );
  }

  // Desktop - Switch case based on CMS data...
  if (!authDesktopViewType) {
    return <LoadingLogo />;
  }

  // Get the appropriate desktop app variant based on configuration
  const AppDesktop = getAppDesktop(authDesktopViewType);

  return (
    <Suspense fallback={<LoadingLogo />}>
      <AppDesktop />
    </Suspense>
  );
};

App.propTypes = propTypes;

export default withCms(App);
